/**
 * Partial backorder notice on the product page.
 *
 * Shows a line above the quantity selector when the shopper asks for more units than the
 * selected variant holds on hand and that variant can still be oversold. Nothing here
 * restricts the cart: the full requested quantity stays addable, the notice only sets the
 * expectation that part of it will arrive as a backorder.
 *
 * Inventory levels are not exposed by `product.variants | json`, so the numbers come from
 * the `#variants_inventory_quantity` payload the variant picker renders. Switching variants
 * re-renders that payload server-side, which is why it is re-read on every update instead
 * of being cached.
 */
(function () {
  // The "many" copy is rendered through the translate filter with this placeholder standing
  // in for the count, because the real count is only known once the shopper picks one.
  const COUNT_PLACEHOLDER = '__COUNT__';

  function scopeFor(notice) {
    return notice.closest('product-info') || document;
  }

  function quantityInput(notice) {
    const scope = scopeFor(notice);
    return (
      scope.querySelector(`#Quantity-${notice.dataset.section}`) ||
      scope.querySelector('.quantity__input')
    );
  }

  function selectedVariantId(notice) {
    const scope = scopeFor(notice);
    const input =
      scope.querySelector(`#product-form-${notice.dataset.section} input[name="id"]`) ||
      scope.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  function inventoryEntry(notice) {
    const variantId = selectedVariantId(notice);
    const payload = scopeFor(notice).querySelector('#variants_inventory_quantity');
    if (!payload || !variantId) return null;

    try {
      const entries = JSON.parse(payload.textContent);
      return entries.find((entry) => String(entry.id) === String(variantId)) || null;
    } catch (error) {
      return null;
    }
  }

  function update(notice) {
    const input = quantityInput(notice);
    const entry = inventoryEntry(notice);
    const requested = input ? parseInt(input.value, 10) : NaN;
    const onHand = entry ? Number(entry.inventory_quantity) : 0;
    const canBackorder = entry ? entry.inventory_policy === 'continue' : false;

    // Only the genuinely part-available case gets a message. A variant with nothing on hand
    // is already covered by its `Backorder` swatch label and the pre-order delivery line.
    const show = canBackorder && onHand > 0 && Number.isFinite(requested) && requested > onHand;

    if (!show) {
      notice.textContent = '';
      notice.classList.add('hidden');
      return;
    }

    const template = onHand === 1 ? notice.dataset.messageOne : notice.dataset.messageMany;
    notice.textContent = (template || '').replace(COUNT_PLACEHOLDER, String(onHand));
    notice.classList.remove('hidden');
  }

  function bind(notice) {
    const input = quantityInput(notice);

    if (input && !input.dataset.backorderNoticeBound) {
      // `change` covers the +/- buttons, which dispatch it after stepping the input;
      // `input` covers typing a quantity directly.
      ['input', 'change'].forEach((eventName) => {
        input.addEventListener(eventName, () => update(notice));
      });
      input.dataset.backorderNoticeBound = 'true';
    }

    update(notice);
  }

  function refreshAll() {
    document.querySelectorAll('[data-backorder-notice]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAll);
  } else {
    refreshAll();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // A variant change swaps the picker, so both the inventory payload and the selected
    // variant input have already moved on by the time this fires.
    subscribe(PUB_SUB_EVENTS.variantChange, refreshAll);
  }
})();
